import { identity, map, mapObjIndexed } from 'ramda'
import { useCallback, useEffect, useMemo, useState } from 'react'

import _ from 'lodash'

let stateContainer = {}

/**
 * This hook provides state in a similar way as React's `useState` out-of-the-box hook. However, the main difference lies
 * on the fact that this hook provides a refererence to (rather than a copy of) the current state variable.
 *
 * This is useful because whereas in class component state is an internal variable of the underlying object, in function
 * components, state is a local variable of the underlying function. Therefore, while in class components, methods can
 * directly access state via `this.state`, in function components there are no methods, but local functions - closures -
 * where state is accessed as an enclosed variable. This means every time state changes, all closures need to be redefined
 * to 'see' the new state value. However this is computationally very transparent and easy to reason about, the re-definition
 * of closures might be unwanted due to performance reasons (e.g. the closure gets passed down to a component as a handler
 * thus making it re-render more often than needed).
 * In that light, this hook provides a reference to the state object that doesn't change over time, but gives access to
 * an always-updated version of the state variable. For elements that should be re-defined on any state changes,
 * just mark `stateContainer.state` as a dependency
 * @param initialState: The initial state value
 * @param stateId: An identifier for the state object.
 * @returns [stateContainer, stateMutator]
 */
const useStateContainer = (formId, initialState, stateId) => {
    const [form, setForm] = useState(formId)
    const [state, setState] = useState(initialState)

    useEffect(() => {
        if (formId !== form) {
            setForm(formId)
            stateContainer['formId'] = formId
            setState(initialState)
        }
    }, [form, formId, initialState])

    //Similar as how hooks are implemented, state container lives in a local variable that is shared among function components
    //Different hooks are implemented as a linked list (that's why they rely so much on calling order). Here, to avoid
    //order-problems, state containers are given an explicit identifier.
    if (!stateContainer[stateId]) {
        stateContainer[stateId] = {}
    }

    stateContainer[stateId].state = state
    return [stateContainer[stateId], setState]
}

/**
 * This hook provides out-of-the-box form-handling logic.
 *
 * It takes an object whose values are used to initially fill the values of the form and whose structure defines the very
 * structure of the form fields themselves. E.g. If you want a form with two fields 'name' and 'age' you can pass an object
 * like `{name: 'John', age: 27}`.
 * It also takes a Yup validation schema (see https://github.com/jquense/yup) and an `onSubmit` function that will receive
 * the values of the form fields and perform a submission action.
 *
 * It returns an object composed of various relevant properties:
 *  - values: These are the values of the form fields. This object mimics `initialValues` but evolves in time. E.g. for the
 *  initial values used before, this object will be initialized as `{name: 'John', age: 27}` and, as value/event handlers
 *  (see below) are invoked, it will change its values, like `{name: 'John', age: 29}`, always keeping the same structure
 *  - valueHandlers: This object stores a set of functions that receive a value and update the `values` object accordingly.
 *  E.g. invoking `valueHandlers.age(37)` will result in `values` being `{name: 'John', age: 37}`. This object also mimics
 *  the structure of `initialValues`
 *  - eventHandlers: This object is similar to `valueHandlers` but receives a DOM event instead of a value and sets the
 *  value of the event's target as the field's value. It is basically a shorthand to avoid extracting the value from
 *  `event.target.value` when you're dealing with native inputs
 *  - touched: This object mimics the structure of `initialValues` but provides true/false flags to indicate if a field
 *  has been edited somehow. Whenever a valueHandler is called, the corresponding key is set to true in this object.
 *  E.g if after initialization only `valueHandlers.age(37)` is invoked, then touched will be `{age: true}`. For `eventHandlers`
 *  the touched flag is set upon the user unfocusing the input.
 *  - changed: This object is similar to `touched` but the difference is that this object tracks whenever a value has been
 *  changed. This means it never waits for unfocus and if the value inputted eventually is the same as the initialValue,
 *  it will be false. Also, it includes an additional `form` key that indicates if any of the values in the form has been
 *  changed. E.g if after initialization `valueHandlers.age(37)` and `valueHandlers.name('John')` are invoked, then changed
 *  will be `{age: true, form: true}` whereas `touched` will be `{age: true, name: true}`.
 *  - errors: This object contains the errors arisen from performing Yup validation. Whenever a field is changed, validation
 *  for the whole form is run and errors are included. To control when to display the errors leverage the `changed` and
 *  `touched` objects. E.g if the `validationSchema` restricted users to be 35 or younger, invoking `valueHandlers.age(37)`
 *  will leave the `errors` object as `{age: 'Whatever error message you set in the schema'}`. To avloid displaying this error
 *  message until user unfocuses the text input, you can do `(touched.age && errors.age)`
 *  - handleSubmit: This function is intended to be called when the form is to be submitted. It basically sets all fields
 *  as touched, runs validations and calls `onSubmit` if and only if all validations are passed
 *
 * @param initialValues: any
 * @param onSubmit: (any) => any
 * @param validationSchema: Yup validation schema, see https://github.com/jquense/yup for more details
 * @returns {{valueHandlers: *, handleSubmit: *, touched: *, values: *, eventHandlers: *, errors: *, changed: *}}
 */

export const useForm = (formId, initialValues, onSubmit, validationSchema) => {
    const [values, setValues] = useStateContainer(formId, initialValues, formId + 'values')
    const [errors, setErrors] = useStateContainer(formId, {}, formId + 'errors')
    const [touched, setTouched] = useStateContainer(formId, {}, formId + 'touched')
    const [changed, setChanged] = useStateContainer(formId, {}, formId + 'changed')

    const setValidationErrors = validationErrors => {
        let condensedErrors = {}
        validationErrors.inner.forEach(error => (condensedErrors[error.path] = error.message))
        setErrors(condensedErrors)
    }

    const runValidations = _.debounce((newValues, afterValidation = () => {}) => {
        if (!validationSchema) {
            afterValidation(newValues)
        } else {
            validationSchema
                .validate(newValues, { abortEarly: false })
                .then(() => {
                    setErrors({})
                    afterValidation(newValues)
                })
                .catch(setValidationErrors)
        }
    }, 600)

    const valueSetterFor = (key, afterValue) => newValue => {
        const newValues = { ...values.state, [key]: newValue }
        setValues(newValues)
        setChanged({ ...changed.state, [key]: initialValues[key] !== newValue })
        afterValue && afterValue(key, newValue)
        runValidations(newValues)
    }
    const setTouchedKey = key => setTouched({ ...touched.state, [key]: true })

    const makeValueHandler = (_, handlerKey) => valueSetterFor(handlerKey, setTouchedKey)
    const makeEventHandler = (_, handlerKey) => ({ target }) => {
        valueSetterFor(handlerKey)(target.value)

        const handleBlur = () => {
            setTouchedKey(handlerKey)
            target && target.removeEventListener('focusout', handleBlur)
        }

        target.addEventListener('focusout', handleBlur)
    }

    //As value handler need to access the state to mutate it, using a normal state variable by means of `useState` would
    //mean every time some state field is mutated, the whole state object would be mutated and therefore all depending
    //handlers should be redefined, even when the value they care for hasn;t changed. Since we cannot separate form fields
    //in different state variables (because we cannot call hooks in a loop, and form fields are a parameter so we don't know
    //how many we'll need), we need to keep the single-object state but make sure handlers are not unnecessarily re-defined
    //(because their redefinition leads to form field components being unnecessarily re-rendered). To achieve this, we use
    //the `useStateContainer` that provides a reference to the state object that doesn't change over time, but gives access
    //to the updated object via `.state`. Also, we explicitly memoize handlers without dependencies so they never get redefined.
    //We need to add an exception rule because this solution is slightly deviating from the 'pure' nature of function components
    //and hooks, relying on references tu mutable objects.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const valueHandlers = useMemo(() => mapObjIndexed(makeValueHandler, values.state), [stateContainer.formId])
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const eventHandlers = useMemo(() => mapObjIndexed(makeEventHandler, values.state), [stateContainer.formId])

    const handleSubmit = useCallback(
        e => {
            e.preventDefault()
            setTouched(map(_ => true, initialValues))
            runValidations(values.state, onSubmit)
        },
        [initialValues, onSubmit, runValidations, setTouched, values.state],
    )

    const formChanged = Object.values(changed.state).some(identity)

    return {
        values: values.state,
        handleSubmit,
        valueHandlers,
        eventHandlers,
        errors: errors.state,
        touched: touched.state,
        changed: { ...changed.state, form: formChanged },
    }
}
